import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { Article, Articles } from "../RoomSettings/types/article";
import { LocalCombinationItem } from "@coworker/apprestructured/src/shared/types/localCombinationItem";

export const MAX_QUANTITY_NUMBER = 1000;

export enum ParseStatus {
  ParseOk = "ParseOk",
  ParseErrorArticleNumber = "ParseErrorArticleNumber",
  ParseErrorQuantity = "ParseErrorQuantity",
}

type ProductArticleIdAndQuantity = {
  productArticleId: string;
  quantity: number;
  parentArticle?: string;
  articlesCountInCombination?: number;
};

type ValidateInputOptions = {
  validateArticleNumber?: boolean;
};

const cleanUpArticleId = (articleId: string) => {
  return articleId.replace(/\./g, "").replace(/-/g, "").trim();
};

export const extractProductArticleIdAndQuantity = (
  inputValue: string
): ProductArticleIdAndQuantity[] => {
  return inputValue
    .trim()
    .replace(/\t/g, " ")
    .replace(/ +/g, " ")
    .split(/\n/)
    .map((line) => {
      const [productArticleId, quantity] = line.trim().split(/\s+/);
      const cleanedId = cleanUpArticleId(productArticleId ?? "");
      return {
        productArticleId: cleanedId,
        quantity: quantity ? parseInt(quantity, 10) : 1,
      };
    })
    .filter((article) => article !== null);
};

export const isValidInput = (
  inputValue: string,
  { validateArticleNumber = true }: ValidateInputOptions = {}
): ParseStatus => {
  if (!inputValue) return ParseStatus.ParseErrorArticleNumber;

  const lines = inputValue.trim().split(/\n/);
  const productArticleIdRegex = /^[0-9.-]+$/;
  const quantityRegex = /^[0-9]+$/;

  for (const line of lines) {
    const [productArticleId, quantity] = line.trim().split(/\s+/);
    const rawProductArticleId = productArticleId ?? "";
    const cleanedProductArticleId = cleanUpArticleId(rawProductArticleId);

    const isArticleNumber =
      productArticleIdRegex.test(rawProductArticleId) &&
      cleanedProductArticleId.length === 8;

    if (validateArticleNumber && !isArticleNumber) {
      return ParseStatus.ParseErrorArticleNumber;
    }

    if (quantity) {
      if (!quantityRegex.test(quantity)) {
        return ParseStatus.ParseErrorQuantity;
      }
      const quantityNumber = parseInt(quantity, 10);
      if (quantityNumber < 1 || quantityNumber > MAX_QUANTITY_NUMBER) {
        return ParseStatus.ParseErrorQuantity;
      }
    }
  }

  return ParseStatus.ParseOk;
};

export const transformArticlesInfos = (
  mediaId: string,
  articlesToAdd: ProductArticleIdAndQuantity[],
  articlesInfos: (Item | LocalCombinationItem)[]
): Articles => {
  const mergedArticles: Article[] = [];
  for (const articleToAdd of articlesToAdd) {
    const articleInfo = articlesInfos?.find(
      (articleInfo) => articleInfo?.no === articleToAdd.productArticleId
    );

    const hasName = !!articleInfo?.name;
    const hasType = !!articleInfo?.type;

    if (!articleInfo || !hasName || !hasType) continue;

    mergedArticles.push({
      active: "ACTIVE",
      hfb: articleInfo?.hfbNo || "Unknown",
      nbrArticles: articleToAdd.quantity,
      itemName: articleInfo?.name || "Unknown",
      itemType: articleInfo?.description ?? "Unknown",
      itemColor: articleInfo?.description?.split(",")?.[1] || "Unknown",
      imageSmall:
        articleInfo?.smallImageUrl || articleInfo?.imageUrl || "Unknown",
      productArticleType: articleInfo?.type || "ART",
      productArticleId: articleToAdd.productArticleId,
      mediaId: mediaId || "",
      articlesCountInCombination:
        (articleInfo as LocalCombinationItem)?.itemsCountInCombination ||
        articleToAdd.articlesCountInCombination ||
        0,
      parentArticleCombinationNumber: articleToAdd.parentArticle || "",
    } as Article);
  }
  return mergedArticles;
};

export const removeDuplicateArticles = (articles: Articles) => {
  const articleMap = new Map<string, Article>();
  const result: Article[] = [];

  for (const article of articles) {
    // If the article has a parent, it is part of a combination, same article can exist multiple times
    if (article.parentArticleCombinationNumber) {
      result.push(article);
      continue;
    }
    if (articleMap.has(article.productArticleId)) {
      articleMap.get(article.productArticleId)!.nbrArticles +=
        article.nbrArticles;
    } else {
      articleMap.set(article.productArticleId, article);
    }
  }
  return [...articleMap.values(), ...result];
};
